
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { OxyNestedSelector, OxySelector } from "@/oxygen/classes/lib/types";
import Icon from "@/components/UI/Icon.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import { AVAILABLE_STATES } from "@/oxygen/classes/lib/classes";
import { VDivider, VListItem, VSubheader } from "vuetify/lib/components";

type Item = {
  id?: string;
  label?: string;
  header?: boolean;
  divider?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  action?: () => void;
  nudge?: boolean;
  highlight?: boolean;
};

@Component({
  components: {
    Icon,
    Dropdown,
    VDivider,
    VListItem,
    VSubheader
  }
})
export default class OxyClassTokenActions extends Vue {
  @Prop()
  selector?: OxySelector;

  @Prop()
  nestedSelector!: OxyNestedSelector;

  @Prop({ default: false })
  missingSelector!: boolean;

  @Prop({ default: true })
  editable!: boolean;

  @Prop({ default: true })
  creatable!: boolean;

  @Prop({ default: true })
  removable!: boolean;

  @Prop({ default: true })
  lockable!: boolean;

  menuOpen = false;

  offsetX = 0;

  @Watch("menuOpen")
  onMenuOpen() {
    if (this.menuOpen) {
      this.$nextTick(() => {
        const actions = this.$refs.actions as HTMLElement;
        const button = actions.querySelector(
          ".oxy-class-token-actions__button"
        ) as HTMLElement;

        const container = actions.closest(".oxy-class-token") as HTMLElement;

        if (!container) return;

        this.offsetX = container.offsetWidth - button.offsetWidth;
      });
    }
  }

  get items(): Item[] {
    const createNestedSelectorItem = (child: OxyNestedSelector) => ({
      id: child.id,
      label: child.name,
      nudge: true,
      highlight: this.doesNestedSelectorContainValue(child.id),
      action: () => this.$emit("edit-nested-selector", child)
    });

    const createStateSelectorItem = (state: string) => {
      const foundState = this.selector?.children?.find(
        child => child.name === state
      );

      return {
        id: foundState?.id || state,
        label: state,
        nudge: true,
        highlight: foundState
          ? this.doesNestedSelectorContainValue(foundState?.id)
          : false,
        action: () => this.$emit("edit-state", state)
      };
    };

    const states = AVAILABLE_STATES.map(createStateSelectorItem);

    const nestedSelectors =
      this.selector?.children
        ?.filter(child => !AVAILABLE_STATES.includes(child.name))
        ?.map(createNestedSelectorItem) || [];

    const actions: Item[] = [
      { header: true, label: `${this.selector?.name}` },
      {
        label: "Rename",
        disabled: this.missingSelector,
        action: () => this.$emit("rename")
      },
      {
        label: "Duplicate",
        disabled: this.missingSelector,
        action: () => this.$emit("duplicate")
      },
      this.removable
        ? {
            label: "Remove",
            disabled: !!this.nestedSelector,
            action: () => this.$emit("remove")
          }
        : undefined,
      {
        label: "Delete",
        destructive: true,
        action: () => this.$emit("delete")
      },
      this.lockable
        ? {
            label: this.selector?.locked ? "Unlock" : "Lock",
            action: () => this.$emit("toggle-lock")
          }
        : undefined
    ].filter(Boolean) as Item[];

    const creationActions: Item[] = [
      { divider: true },
      { label: "States", header: true },
      ...states,
      { divider: true },
      { label: "Nested Selectors", header: true },
      ...nestedSelectors,
      {
        label: "Create Selector",
        disabled: this.missingSelector,
        action: () => this.$emit("create-nested-selector")
      }
    ];

    return this.creatable ? [...actions, ...creationActions] : actions;
  }

  doesNestedSelectorContainValue(idOrName: string) {
    const foundSelector = this.selector?.children?.find(
      child => child.name === idOrName || child.id === idOrName
    );
    return !!Object.keys(foundSelector?.properties || {}).length;
  }

  getItemComponent(item: Item) {
    if (item.header) {
      return "v-subheader";
    } else if (item.divider) {
      return "v-divider";
    } else {
      return "v-list-item";
    }
  }

  isSelected(item: Item) {
    const nested = this.nestedSelector;
    if (!nested || !item.id) return false;
    if (nested?.id === item?.id) return true;
    if (nested?.name === item?.id) return true;
    return false;
  }
}
